<script>
import { mapState } from "vuex";
import Layout from "@/router/layouts/main";
import { canAccess } from "@/utils/menu-map";
import { TRILHAS } from "@/utils/ranking";

// Regulamento do "Novo modelo de reconhecimento", portado de
// biteams_app_vue/src/router/views/biteams/rk-regras/index.vue. Tela só de conteúdo: não chama API.
//
// Diferenças em relação ao biTeams:
//  - o bloco "Pontos em aberto" NÃO foi trazido: são dúvidas internas da proposta ("precisam de
//    decisão antes da apuração valer"), não regra para o consultor;
//  - todo o texto está no template (sem v-html) e cada trilha ganhou atalho para o ranking,
//    exibido só se a pessoa (ou a secretária) tiver acesso à tela.

const PESOS_CONSULTOR = [
  { item: "Produção da própria base", pts: "1 ponto" },
  { item: "Consultor ativo", pts: "2 pontos" },
  { item: "Formação de Consultor Líder", pts: "5 pontos" },
];

const PESOS_GERENTE = [
  { item: "Produção da própria base", pts: "1 ponto" },
  { item: "Produção dos diretos", pts: "0,5 ponto" },
  { item: "Consultor ativo", pts: "2 pontos" },
  { item: "Formação de Consultor Líder", pts: "5 pontos" },
  { item: "Formação de Regional", pts: "15 pontos", destaque: true },
];

const ATALHOS = [
  { trilha: TRILHAS.consultores, icone: "bx bx-user", desc: "Consultores da base, apurados por pontos todo mês." },
  { trilha: TRILHAS.home, icone: "bx bx-home-alt", desc: "Regionais home office, com ranking próprio." },
  { trilha: TRILHAS.executiva, icone: "bx bx-buildings", desc: "Regionais do nível executivo para cima." },
  { trilha: TRILHAS.gerentes, icone: "bx bx-medal", desc: "Núcleo de gerentes, apurado a cada trimestre." },
];

const PLACAS = ["600", "1.000", "2.000", "3.000", "4.000"];

const MUDANCAS = [
  { trilha: "Consultor", sai: ["Consultor multiplicador", "Sorteio do TOP consultor"], muda: ["Valor de premiação", "Formato de contagem"], fica: ["R$ 5 mil para os 5 primeiros"] },
  { trilha: "Regionais Home Office", sai: [], muda: ["Formato de contagem", "Valor de premiação: R$ 5.000,00"], fica: ["Placa de ativos"] },
  { trilha: "Regionais Executivo e acima", sai: ["Rankings por indicadores"], muda: ["Formato de contagem"], fica: ["Regional formador", "Placa de ativos"] },
  { trilha: "Núcleo (Gerentes)", sai: [], muda: ["Formato de contagem"], fica: [] },
];

export default {
  components: { Layout },
  data() {
    return { PESOS_CONSULTOR, PESOS_GERENTE, ATALHOS, PLACAS, MUDANCAS };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
  },
  methods: {
    podeVer(path) {
      return canAccess(this.account.user, path);
    },
  },
};
</script>

<template>
  <Layout>
    <div class="rr-page">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link tag="a" to="/" aria-label="Início">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
            </svg>
          </router-link>
        </li>
        <li class="breadcrumb-item">Rankings</li>
        <li class="breadcrumb-item d-none d-sm-block">Regras</li>
      </ol>

      <div class="page-title-box mb-3">
        <h3 class="mb-1">Regras e regulamento</h3>
        <p class="rr-intro mb-0">
          Novo modelo de reconhecimento: <strong>o ano da multiplicação</strong>. As premiações passam a
          comunicar indicadores claros de performance, com o reconhecimento separado por níveis e uma
          <strong>pontuação única</strong> em cada ranking.
        </p>
      </div>

      <!-- Trilhas -->
      <section class="card" aria-labelledby="rr-trilhas">
        <div class="card-body">
          <h2 id="rr-trilhas" class="rr-titulo">Níveis de reconhecimento</h2>
          <p class="rr-sub">O reconhecimento é separado em quatro trilhas independentes.</p>
          <ul class="rr-trilhas">
            <li v-for="a in ATALHOS" :key="a.trilha.id" class="rr-trilha">
              <span class="rr-trilha-ico" aria-hidden="true"><i :class="a.icone"></i></span>
              <div class="rr-trilha-corpo">
                <div class="rr-trilha-nome">{{ a.trilha.titulo }}</div>
                <div class="rr-trilha-desc">{{ a.desc }}</div>
                <div class="rr-trilha-meta">
                  <span class="rr-badge" :class="a.trilha.trimestral ? 'is-trimestral' : 'is-mensal'">{{ a.trilha.periodicidade }}</span>
                  <router-link v-if="podeVer(a.trilha.path)" :to="a.trilha.path" class="rr-trilha-link">
                    Ver ranking <i class="bx bx-chevron-right" aria-hidden="true"></i>
                  </router-link>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <!-- O que muda -->
      <section class="card" aria-labelledby="rr-muda">
        <div class="card-body">
          <h2 id="rr-muda" class="rr-titulo">O que muda</h2>
          <div class="rr-cmp">
            <div class="rr-cmp-col">
              <div class="rr-cmp-hd">Antes</div>
              <p>Os indicadores eram somados <strong>separadamente</strong> (produção, consultor ativo, ticket médio), cada um gerando o seu próprio ranking.</p>
            </div>
            <div class="rr-cmp-seta" aria-hidden="true"><i class="bx bx-right-arrow-alt"></i></div>
            <div class="rr-cmp-col is-agora">
              <div class="rr-cmp-hd">Agora</div>
              <p>Entra um <strong>processo de pontos único</strong>. Cada participante acompanha no sistema, com possibilidade de conferência, qual a sua posição e qual a sua produção.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Pesos -->
      <section class="card" aria-labelledby="rr-pesos">
        <div class="card-body">
          <h2 id="rr-pesos" class="rr-titulo">Pesos da pontuação</h2>
          <p class="rr-sub">
            Os pesos fortalecem a cultura da multiplicação sem perder o foco na produção. Para os gerentes,
            a formação é o principal objetivo, por isso o peso maior.
          </p>
          <div class="rr-pesos-grid">
            <div>
              <h3 class="rr-pesos-hd">Consultor e Regional</h3>
              <table class="table table-sm rr-pesos-tb mb-0">
                <thead class="thead-light"><tr><th scope="col">Indicador</th><th scope="col" class="text-right">Pontos</th></tr></thead>
                <tbody>
                  <tr v-for="p in PESOS_CONSULTOR" :key="p.item">
                    <td>{{ p.item }}</td>
                    <td class="text-right"><span class="rr-pts">{{ p.pts }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <h3 class="rr-pesos-hd">Gerentes (núcleo)</h3>
              <table class="table table-sm rr-pesos-tb mb-0">
                <thead class="thead-light"><tr><th scope="col">Indicador</th><th scope="col" class="text-right">Pontos</th></tr></thead>
                <tbody>
                  <tr v-for="p in PESOS_GERENTE" :key="p.item" :class="{ 'rr-destaque': p.destaque }">
                    <td>{{ p.item }}</td>
                    <td class="text-right"><span class="rr-pts">{{ p.pts }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="rr-nota">
            <i class="bx bx-info-circle" aria-hidden="true"></i>
            <span>A formação de novos consultores é contada <strong>apenas uma vez</strong>, e para o consultor direto a ele.</span>
          </div>
        </div>
      </section>

      <!-- Regulamento: Consultores -->
      <section class="card" aria-labelledby="rr-reg-consultores">
        <div class="card-body">
          <div class="rr-hd-row">
            <h2 id="rr-reg-consultores" class="rr-titulo mb-0">TOP 20 Consultores</h2>
            <span class="rr-badge is-mensal">Mensal</span>
          </div>
          <p class="rr-sub">Ranking de consultores por pontuação, apurado todo mês.</p>
          <dl class="rr-dl">
            <dt>Base de cálculo</dt>
            <dd>Os primeiros boletos das adesões do <strong>mês anterior</strong>, pagos no <strong>mês da apuração</strong>.</dd>
            <dt>Premiação</dt>
            <dd><strong>R$ 5 mil</strong> para os 5 primeiros colocados.</dd>
            <dt>Pesos aplicados</dt>
            <dd>Tabela "Consultor e Regional".</dd>
          </dl>
          <div class="rr-nota">
            <i class="bx bx-error" aria-hidden="true"></i>
            <span>Quando o consultor atingir <strong>300 ativos</strong> na base, ele participa no mês em que atingiu e, a partir do próximo, sai desta premiação, <strong>mesmo que a base caia depois</strong>.</span>
          </div>
        </div>
      </section>

      <!-- Regulamento: Home Office -->
      <section class="card" aria-labelledby="rr-reg-home">
        <div class="card-body">
          <div class="rr-hd-row">
            <h2 id="rr-reg-home" class="rr-titulo mb-0">TOP 20 Regionais Home Office</h2>
            <span class="rr-badge is-mensal">Mensal</span>
          </div>
          <p class="rr-sub">Ranking de regionais home office por pontuação, apurado todo mês.</p>
          <dl class="rr-dl">
            <dt>Hierarquia</dt>
            <dd>Regionais <strong>Home Office</strong>.</dd>
            <dt>Base de cálculo</dt>
            <dd>Os primeiros boletos das adesões do <strong>mês anterior</strong>, pagos no <strong>mês da apuração</strong>.</dd>
            <dt>Premiação</dt>
            <dd><strong>R$ 5.000,00</strong>.</dd>
            <dt>Pesos aplicados</dt>
            <dd>Tabela "Consultor e Regional".</dd>
          </dl>
          <div class="rr-nota">
            <i class="bx bx-error" aria-hidden="true"></i>
            <span>Quando a regional Home Office atingir <strong>500 ativos</strong> na base, ela participa no mês em que atingiu e, a partir do próximo, sai desta premiação, <strong>mesmo que a base caia depois</strong>. Ao sair, ela <strong>deixa de participar do ranking</strong>: não sobe automaticamente para a trilha Executivo. A mudança de nível é um processo à parte.</span>
          </div>
        </div>
      </section>

      <!-- Regulamento: Executivo -->
      <section class="card" aria-labelledby="rr-reg-executiva">
        <div class="card-body">
          <div class="rr-hd-row">
            <h2 id="rr-reg-executiva" class="rr-titulo mb-0">TOP 20 Regionais Executivo e acima</h2>
            <span class="rr-badge is-mensal">Mensal</span>
          </div>
          <p class="rr-sub">Mesmo regulamento do TOP 20 Home Office; muda apenas a hierarquia apurada.</p>
          <dl class="rr-dl">
            <dt>Hierarquia</dt>
            <dd>Todas as regionais do nível <strong>Executivo para cima</strong>.</dd>
            <dt>Base de cálculo</dt>
            <dd>Os primeiros boletos das adesões do <strong>mês anterior</strong>, pagos no <strong>mês da apuração</strong>.</dd>
            <dt>Premiação</dt>
            <dd><strong>R$ 5.000,00</strong>.</dd>
            <dt>Pesos aplicados</dt>
            <dd>Tabela "Consultor e Regional".</dd>
            <dt>Reconhecimento</dt>
            <dd>Mantém <strong>Regional Formador</strong> e <strong>Placa de Ativos</strong>.</dd>
          </dl>
        </div>
      </section>

      <!-- Regulamento: Gerentes -->
      <section class="card" aria-labelledby="rr-reg-gerentes">
        <div class="card-body">
          <div class="rr-hd-row">
            <h2 id="rr-reg-gerentes" class="rr-titulo mb-0">TOP 20 Gerentes</h2>
            <span class="rr-badge is-trimestral">Trimestral</span>
          </div>
          <p class="rr-sub">Ranking do núcleo de gerentes, apurado a cada trimestre.</p>
          <dl class="rr-dl">
            <dt>Regra</dt>
            <dd>Pontuação considerando também a soma da produção dos diretos.</dd>
            <dt>Reconhecimento</dt>
            <dd>Deslocamento para o <strong>encontro trimestral</strong> + <strong>reunião estratégica</strong>.</dd>
            <dt>Pesos aplicados</dt>
            <dd>Tabela "Gerentes (núcleo)".</dd>
          </dl>
        </div>
      </section>

      <!-- Regional Formador -->
      <section class="card" aria-labelledby="rr-reg-formador">
        <div class="card-body">
          <div class="rr-hd-row">
            <h2 id="rr-reg-formador" class="rr-titulo mb-0">Regional Formador</h2>
            <span class="rr-badge is-sorteio">Sorteio mensal</span>
          </div>
          <p class="rr-sub">A regional que formar consultores líderes ativos participa de um sorteio.</p>
          <dl class="rr-dl">
            <dt>Como participa</dt>
            <dd>A cada consultor líder formado, <strong>mais 1 chance</strong> de ganhar.</dd>
            <dt>Prêmio</dt>
            <dd><strong>Experiência Teams</strong>.</dd>
          </dl>
        </div>
      </section>

      <!-- Placas -->
      <section class="card" aria-labelledby="rr-placas">
        <div class="card-body">
          <div class="rr-hd-row">
            <h2 id="rr-placas" class="rr-titulo mb-0">Placas de Ativos</h2>
            <span class="rr-badge is-trimestral">Trimestral</span>
          </div>
          <p class="rr-sub">
            Apuração trimestral, com o ciclo começando em <strong>janeiro de cada ano</strong>. A regional que
            atingir a quantidade de ativos é reconhecida no evento do estado com a Placa de Ativos.
          </p>
          <ul class="rr-placas">
            <li v-for="p in PLACAS" :key="p" class="rr-placa">
              <i class="bx bx-trophy" aria-hidden="true"></i>
              <span class="rr-placa-val">{{ p }}</span>
              <span class="rr-placa-lbl">ativos</span>
            </li>
          </ul>
          <div class="rr-nota">
            <i class="bx bx-error" aria-hidden="true"></i>
            <span>O fechamento do relatório é feito apenas no <strong>último dia do mês</strong>. Se a meta for atingida no meio do mês mas, no dia da apuração, estiver abaixo, vale o valor do dia da apuração.</span>
          </div>
        </div>
      </section>

      <!-- Sai / Muda / Fica -->
      <section class="card" aria-labelledby="rr-mudancas">
        <div class="card-body">
          <h2 id="rr-mudancas" class="rr-titulo">O que sai, muda e fica em cada trilha</h2>
          <p class="rr-sub">Resumo das alterações em relação ao modelo anterior.</p>
          <div class="rr-mudancas">
            <div v-for="m in MUDANCAS" :key="m.trilha" class="rr-mudanca">
              <h3 class="rr-mudanca-nome">{{ m.trilha }}</h3>
              <dl class="rr-mudanca-dl">
                <dt>Sai</dt>
                <dd>
                  <span v-for="s in m.sai" :key="s" class="rr-tag is-sai">{{ s }}</span>
                  <span v-if="!m.sai.length" class="rr-vazio">Nada</span>
                </dd>
                <dt>Muda</dt>
                <dd><span v-for="s in m.muda" :key="s" class="rr-tag is-muda">{{ s }}</span></dd>
                <dt>Fica</dt>
                <dd>
                  <span v-for="s in m.fica" :key="s" class="rr-tag is-fica">{{ s }}</span>
                  <span v-if="!m.fica.length" class="rr-vazio">Nada</span>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </section>
    </div>
  </Layout>
</template>

<style scoped>
.rr-page { max-width: 1100px; }
.rr-intro { color: var(--st-text-muted); line-height: 1.6; }
.rr-intro strong { color: var(--st-text); }

.rr-titulo { font-size: 16px; font-weight: 700; color: var(--st-text); margin-bottom: 4px; }
.rr-sub { font-size: 13px; color: var(--st-text-muted); line-height: 1.6; margin-bottom: 14px; }
.rr-sub strong { color: var(--st-text); }
.rr-hd-row { display: flex; align-items: center; justify-content: space-between; gap: 8px 12px; flex-wrap: wrap; margin-bottom: 4px; }

.rr-badge {
  display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  padding: 3px 10px; border-radius: 999px; white-space: nowrap;
}
.rr-badge.is-mensal { color: var(--st-brand); background: var(--st-brand-soft); }
.rr-badge.is-trimestral { color: var(--st-warning-strong); background: var(--st-warning-soft); }
.rr-badge.is-sorteio { color: var(--st-success-strong); background: var(--st-success-soft); }

/* Trilhas */
.rr-trilhas {
  list-style: none; margin: 0; padding: 0;
  display: grid; grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr)); gap: 10px;
}
.rr-trilha {
  display: flex; gap: 12px; align-items: flex-start; padding: 14px;
  border: 1px solid var(--st-border-soft); border-radius: var(--st-radius); background: var(--st-canvas);
}
.rr-trilha-ico {
  flex: 0 0 auto; width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: var(--st-brand); background: var(--st-brand-soft);
}
.rr-trilha-corpo { min-width: 0; flex: 1 1 auto; }
.rr-trilha-nome { font-size: 14px; font-weight: 700; color: var(--st-text); }
.rr-trilha-desc { font-size: 12px; color: var(--st-text-muted); line-height: 1.5; margin: 2px 0 8px; }
.rr-trilha-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-wrap: wrap; }
.rr-trilha-link {
  display: inline-flex; align-items: center; min-height: 36px;
  font-size: 13px; font-weight: 600; color: var(--st-brand);
}

/* Antes -> Agora */
.rr-cmp { display: flex; align-items: stretch; gap: 12px; }
.rr-cmp-col {
  flex: 1 1 0; padding: 14px 16px; border-radius: var(--st-radius);
  border: 1px solid var(--st-border-soft); background: var(--st-canvas);
}
.rr-cmp-col p { font-size: 13px; line-height: 1.6; color: var(--st-text-muted); margin: 0; }
.rr-cmp-col strong { color: var(--st-text); }
.rr-cmp-col.is-agora { border-color: var(--st-brand); background: var(--st-brand-soft); }
.rr-cmp-hd { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; color: var(--st-text-muted); }
.rr-cmp-col.is-agora .rr-cmp-hd { color: var(--st-brand); }
.rr-cmp-seta { display: flex; align-items: center; font-size: 22px; color: var(--st-text-soft); }

/* Pesos */
.rr-pesos-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr)); gap: 16px; }
.rr-pesos-hd { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--st-text-muted); margin-bottom: 8px; }
.rr-pesos-tb { font-size: 13px; }
.rr-pesos-tb .rr-destaque td { background: var(--st-brand-soft); }
.rr-pts { font-weight: 700; color: var(--st-brand); white-space: nowrap; }

/* Definições */
.rr-dl { display: grid; grid-template-columns: max-content 1fr; gap: 8px 18px; margin: 0; font-size: 13px; line-height: 1.6; }
.rr-dl dt { font-weight: 600; color: var(--st-text-muted); }
.rr-dl dd { margin: 0; color: var(--st-text); }

.rr-nota {
  display: flex; gap: 8px; align-items: flex-start; margin-top: 14px; padding: 10px 12px;
  border-radius: var(--st-radius); font-size: 13px; line-height: 1.55;
  background: var(--st-warning-soft); color: var(--st-text);
}
.rr-nota i { font-size: 18px; line-height: 1.2; color: var(--st-warning-strong); flex-shrink: 0; }

/* Placas */
.rr-placas { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fit, minmax(96px, 1fr)); gap: 8px; }
.rr-placa {
  display: flex; flex-direction: column; align-items: center; padding: 12px 8px;
  border: 1px solid var(--st-border-soft); border-radius: var(--st-radius); background: var(--st-canvas);
}
.rr-placa i { font-size: 18px; color: var(--st-warning-strong); }
.rr-placa-val { font-size: 17px; font-weight: 700; color: var(--st-text); }
.rr-placa-lbl { font-size: 11px; color: var(--st-text-muted); text-transform: uppercase; letter-spacing: 0.05em; }

/* Sai / muda / fica */
.rr-mudancas { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 10px; }
.rr-mudanca { padding: 12px 14px; border: 1px solid var(--st-border-soft); border-radius: var(--st-radius); }
.rr-mudanca-nome { font-size: 14px; font-weight: 700; color: var(--st-text); margin-bottom: 8px; }
.rr-mudanca-dl { margin: 0; display: grid; grid-template-columns: 48px 1fr; gap: 6px 8px; align-items: baseline; }
.rr-mudanca-dl dt { font-size: 11px; font-weight: 700; text-transform: uppercase; color: var(--st-text-muted); }
.rr-mudanca-dl dd { margin: 0; }
.rr-tag { display: inline-block; font-size: 12px; padding: 2px 8px; border-radius: 4px; margin: 0 4px 4px 0; }
.rr-tag.is-sai { color: var(--st-danger-strong); background: var(--st-danger-soft); text-decoration: line-through; }
.rr-tag.is-muda { color: var(--st-warning-strong); background: var(--st-warning-soft); }
.rr-tag.is-fica { color: var(--st-success-strong); background: var(--st-success-soft); }
.rr-vazio { font-size: 12px; color: var(--st-text-soft); }

@media (max-width: 575.98px) {
  .rr-cmp { flex-direction: column; }
  .rr-cmp-seta { justify-content: center; transform: rotate(90deg); }
  .rr-dl { grid-template-columns: 1fr; gap: 2px; }
  .rr-dl dd { margin-bottom: 8px; }
}
</style>
